import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'

const STATUS_COLORS = { draft: 'neutral', sent: 'info', paid: 'success', overdue: 'danger', cancelled: 'neutral' }
const STATUS_LABELS = { draft: 'Draft', sent: 'Due', paid: 'Paid ✓', overdue: 'Overdue', cancelled: 'Cancelled' }

function Modal({ title, onClose, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="card animate-fadeUp" style={{ width: '100%', maxWidth: 560, maxHeight: '90vh', overflow: 'auto', padding: 0 }}>
        <div style={{ padding: '18px 22px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ fontSize: 17 }}>{title}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, color: '#62615D', cursor: 'pointer' }}>×</button>
        </div>
        <div style={{ padding: '22px' }}>{children}</div>
      </div>
    </div>
  )
}
function InvoiceView({ invoice, onClose }) {
  const lineItems = invoice.line_items || []
  const subtotal = lineItems.reduce((s, l) => s + ((l.qty || 1) * (l.rate || 0)), 0)
  const tax = invoice.tax_rate ? subtotal * (invoice.tax_rate / 100) : 0
  const total = invoice.total_amount || subtotal + tax

  return (
    <div>
      <div style={{ background: '#202326', borderRadius: 10, padding: '20px 24px', marginBottom: 20, color: '#fff' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 22, fontWeight: 700, color: '#CBB17B' }}>INVOICE</div>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', marginTop: 2 }}>{invoice.invoice_number}</div>
          </div>
          <span className={`badge badge-${STATUS_COLORS[invoice.status]}`}>{STATUS_LABELS[invoice.status]}</span>
        </div>
        <div style={{ display: 'flex', gap: 24, marginTop: 16 }}>
          {invoice.issue_date && (
            <div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>Issue Date</div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{new Date(invoice.issue_date).toLocaleDateString()}</div>
            </div>
          )}
          {invoice.due_date && (
            <div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>Due Date</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: new Date(invoice.due_date) < new Date() && invoice.status !== 'paid' ? '#F87171' : '#fff' }}>
                {new Date(invoice.due_date).toLocaleDateString()}
              </div>
            </div>
          )}
        </div>
      </div>

      {invoice.description && (
        <div style={{ background: '#F7F3EB', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#4a5568' }}>
          {invoice.description}
        </div>
      )}

      <div className="client-invoice-table-scroll" style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16, minWidth: 520 }}>
        <thead>
          <tr style={{ background: '#F7F3EB' }}>
            {['Service', 'Qty', 'Rate', 'Amount'].map(h => (
              <th key={h} style={{ padding: '8px 12px', textAlign: h === 'Service' ? 'left' : 'right', fontSize: 11, fontWeight: 700, color: '#62615D', textTransform: 'uppercase' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {lineItems.map((item, i) => (
            <tr key={i} style={{ borderBottom: '1px solid #F7F3EB' }}>
              <td style={{ padding: '10px 12px', fontSize: 13 }}>{item.description}</td>
              <td style={{ padding: '10px 12px', fontSize: 13, textAlign: 'right', color: '#62615D' }}>{item.qty}</td>
              <td style={{ padding: '10px 12px', fontSize: 13, textAlign: 'right', color: '#62615D' }}>${parseFloat(item.rate).toFixed(2)}</td>
              <td style={{ padding: '10px 12px', fontSize: 13, textAlign: 'right', fontWeight: 600 }}>${(item.qty * item.rate).toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 20 }}>
        <div style={{ minWidth: 200 }}>
          {invoice.tax_rate > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#62615D', marginBottom: 6 }}>
              <span>Tax ({invoice.tax_rate}%)</span><span>${(subtotal * invoice.tax_rate / 100).toFixed(2)}</span>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', background: '#202326', color: '#CBB17B', padding: '10px 14px', borderRadius: 8, fontSize: 16, fontWeight: 800 }}>
            <span>Total Due</span><span>${total.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {invoice.notes && (
        <div style={{ fontSize: 12, color: '#62615D', marginBottom: 20, paddingTop: 12, borderTop: '1px solid #F7F3EB' }}>
          <strong>Note:</strong> {invoice.notes}
        </div>
      )}

      {(invoice.status === 'sent' || invoice.status === 'overdue') && (
        <button
          className="btn btn-primary"
          style={{ width: '100%', justifyContent: 'center', fontSize: 15, padding: '14px' }}
          onClick={async () => {
            const { data: { session } } = await supabase.auth.getSession()
            const res = await fetch('/.netlify/functions/pay-invoice', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
              body: JSON.stringify({ invoice_id: invoice.id })
            })
            const data = await res.json()
            if (data.url) { window.location.href = data.url }
            else { window.alert(data.error || 'Could not start payment. Please contact your accountant.') }
          }}>
          💳 Pay ${total.toFixed(2)} Now
        </button>
      )}

      {invoice.status === 'paid' && (
        <div style={{ background: '#D1FAE5', border: '1px solid #6EE7B7', borderRadius: 8, padding: '12px 16px', textAlign: 'center', fontSize: 14, fontWeight: 600, color: '#065F46' }}>
          ✅ This invoice has been paid. Thank you!
        </div>
      )}
    </div>
  )
}

export default function ClientInvoicesPage() {
  const { profile, user } = useAuth()
  const [invoices, setInvoices] = useState([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState(null)
  const [activeTab, setActiveTab] = useState('all') // 'all' | 'paid'

  useEffect(() => {
    async function load() {
      const { data: client } = await supabase.from('clients').select('id').eq('portal_user_id', profile?.id || user?.id).maybeSingle()
      if (!client) { setLoading(false); return }
      const { data } = await supabase.from('invoices').select('*')
        .eq('client_id', client.id)
        .neq('status', 'draft')
        .neq('status', 'cancelled')
        .order('created_at', { ascending: false })
      setInvoices(data || [])
      setLoading(false)
    }
    load()
  }, [])

  const totalDue = invoices.filter(i => i.status === 'sent' || i.status === 'overdue').reduce((s, i) => s + (i.total_amount || 0), 0)
  const totalPaid = invoices.filter(i => i.status === 'paid').reduce((s, i) => s + (i.total_amount || 0), 0)

  if (loading) return (
    <div style={{ display: 'grid', gap: 10 }}>
      {[...Array(3)].map((_, i) => <div key={i} className="skeleton" style={{ height: 72 }} />)}
    </div>
  )

  return (
    <div className="client-invoices-root">
      <style>{`
        @media (max-width: 768px) {
          .client-invoices-root .client-invoices-stats {
            grid-template-columns: 1fr !important;
          }
          .client-invoices-root .client-invoice-alert {
            align-items: flex-start !important;
          }
          .client-invoices-root .client-invoice-row {
            flex-wrap: wrap;
          }
          .client-invoices-root .client-invoice-amount {
            text-align: left !important;
          }
        }
      `}</style>
      <div className="client-invoices-stats" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 24 }}>
        {[
          { label: 'Total Invoices', value: invoices.length, color: '#CBB17B', icon: '🧾' },
          { label: 'Amount Due', value: `$${totalDue.toFixed(2)}`, color: totalDue > 0 ? '#F59E0B' : '#10B981', icon: '⏳' },
          { label: 'Total Paid', value: `$${totalPaid.toFixed(2)}`, color: '#10B981', icon: '✅' },
        ].map((s, i) => (
          <div key={i} className="card animate-fadeUp" style={{ padding: '16px 18px', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: s.color }} />
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 22, fontWeight: 700 }}>{s.value}</div>
                <div style={{ fontSize: 12, color: '#62615D', marginTop: 2 }}>{s.label}</div>
              </div>
              <span style={{ fontSize: 22, opacity: 0.6 }}>{s.icon}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', background: '#F7F3EB', borderRadius: 10, padding: 4, marginBottom: 20, gap: 4 }}>
        {[['all', '🧾 All Invoices'], ['paid', '✅ Payment History']].map(([tab, label]) => (
          <button key={tab} onClick={() => setActiveTab(tab)} style={{
            flex: 1, padding: '8px', borderRadius: 8, border: 'none',
            fontSize: 13, fontWeight: 600, transition: 'all 0.15s',
            background: activeTab === tab ? '#202326' : 'transparent',
            color: activeTab === tab ? '#CBB17B' : '#62615D', cursor: 'pointer'
          }}>{label}</button>
        ))}
      </div>

      {totalDue > 0 && activeTab === 'all' && (
        <div className="client-invoice-alert" style={{ background: '#F1E8D7', border: '1px solid #CBB17B', borderRadius: 10, padding: '14px 18px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 24 }}>💳</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>You have ${totalDue.toFixed(2)} outstanding</div>
            <div style={{ fontSize: 12, color: '#62615D' }}>Click on an invoice below to pay securely online</div>
          </div>
        </div>
      )}

      {activeTab === 'paid' ? (
        /* Payment History Tab */
        <div>
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', background: '#F7F3EB', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 13, fontWeight: 700 }}>Payment History</span>
              <span style={{ fontSize: 12, color: '#10B981', fontWeight: 600 }}>Total Paid: ${totalPaid.toFixed(2)}</span>
            </div>
            {invoices.filter(i => i.status === 'paid').length === 0 ? (
              <div style={{ padding: '32px', textAlign: 'center', color: '#62615D', fontSize: 13 }}>No payments yet</div>
            ) : (
              invoices.filter(i => i.status === 'paid').map((inv, idx, arr) => (
                <div key={inv.id} style={{ padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 14, borderBottom: idx < arr.length - 1 ? '1px solid var(--border)' : 'none', cursor: 'pointer' }}
                  onClick={() => setSelected(inv)}>
                  <div style={{ width: 36, height: 36, borderRadius: 8, background: '#D1FAE5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>✅</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 700 }}>{inv.invoice_number}</div>
                    <div style={{ fontSize: 11, color: '#62615D' }}>
                      {inv.paid_at ? `Paid ${new Date(inv.paid_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}` : 'Paid'}
                    </div>
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: '#065F46' }}>${(inv.total_amount || 0).toFixed(2)}</div>
                </div>
              ))
            )}
          </div>
        </div>
      ) : invoices.length === 0 ? (
        <div className="card" style={{ padding: '48px', textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🧾</div>
          <h3 style={{ marginBottom: 8 }}>No invoices yet</h3>
          <p style={{ color: '#62615D', fontSize: 13 }}>Your accountant hasn't sent any invoices yet</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {invoices.map(inv => {
            const total = inv.total_amount || 0
            const overdue = inv.due_date && new Date(inv.due_date) < new Date() && inv.status !== 'paid'
            return (
              <div key={inv.id} className="card client-invoice-row" onClick={() => setSelected(inv)}
                style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer', transition: 'all 0.15s', border: `1px solid ${overdue ? '#F87171' : 'var(--border)'}` }}
                onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-1px)'}
                onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}>
                <div style={{ width: 44, height: 44, borderRadius: 10, background: inv.status === 'paid' ? '#D1FAE5' : '#F1E8D7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>
                  {inv.status === 'paid' ? '✅' : '🧾'}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#202326' }}>{inv.invoice_number}</div>
                  <div style={{ fontSize: 12, color: '#62615D' }}>{inv.description || 'Professional Services'}</div>
                  {inv.due_date && (
                    <div style={{ fontSize: 11, color: overdue ? '#991B1B' : '#62615D', marginTop: 2 }}>
                      {overdue ? '⚠️ Overdue — ' : 'Due '}{new Date(inv.due_date).toLocaleDateString()}
                    </div>
                  )}
                </div>
                <div className="client-invoice-amount" style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: 18, fontWeight: 800, color: '#202326' }}>${total.toFixed(2)}</div>
                  <span className={`badge badge-${STATUS_COLORS[inv.status]}`} style={{ fontSize: 10 }}>
                    {STATUS_LABELS[inv.status]}
                  </span>
                </div>
                {(inv.status === 'sent' || inv.status === 'overdue') && (
                  <button
                    className="btn btn-primary"
                    style={{ fontSize: 12, flexShrink: 0 }}
                    onClick={async e => {
                      e.stopPropagation()
                      const { data: { session } } = await supabase.auth.getSession()
                      const res = await fetch('/.netlify/functions/pay-invoice', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
                        body: JSON.stringify({ invoice_id: inv.id })
                      })
                      const data = await res.json()
                      if (data.url) { window.location.href = data.url }
                      else { window.alert(data.error || 'Could not start payment. Please contact your accountant.') }
                    }}>
                    Pay Now
                  </button>
                )}
              </div>
            )
          })}
        </div>
      )}

      {selected && (
        <Modal title={`Invoice ${selected.invoice_number}`} onClose={() => setSelected(null)}>
          <InvoiceView invoice={selected} onClose={() => setSelected(null)} />
        </Modal>
      )}
    </div>
  )
}
